'use client';

import { JobResponse } from '@/lib/types';
import api from '@/lib/api';

interface JobCardProps {
  job: JobResponse;
  onCancel?: (jobId: string, force?: boolean) => void;
  onRetry?: (jobId: string) => void;
  onDelete?: (jobId: string) => void;
  onSelect?: (job: JobResponse) => void;
  onCopy?: (job: JobResponse) => void;
}

export default function JobCard({ job, onCancel, onRetry, onDelete, onSelect, onCopy }: JobCardProps) {
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCopy) {
      onCopy(job);
    } else {
      navigator.clipboard.writeText(job.prompt);
    }
  };

  return (
    <div
      className="group card cursor-pointer relative"
      onClick={() => onSelect?.(job)}
    >
      {/* Copy button - for completed jobs (left corner) */}
      {job.status === 'completed' && (
        <button
          onClick={handleCopy}
          className="absolute top-2 left-2 z-10 p-1.5 rounded-lg bg-black/60 text-white/80 hover:bg-[var(--accent)] hover:text-black transition-all cursor-pointer"
          title="Copy parameters"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
          </svg>
        </button>
      )}

      {/* Delete button - for completed jobs (right corner) */}
      {job.status === 'completed' && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(job.job_id); }}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-black/60 text-white/80 hover:bg-[var(--error)] hover:text-white transition-all cursor-pointer"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      )}

      {/* Image preview for completed jobs */}
      {job.status === 'completed' && job.images.length > 0 && (
        <div className="aspect-square relative overflow-hidden rounded-t-xl">
          <img
            src={api.getThumbnailUrl(job.images[0])}
            alt={job.prompt}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          {job.images.length > 1 && (
            <div className="absolute bottom-2 right-2 text-xs bg-black/60 px-2 py-1 rounded">
              +{job.images.length - 1}
            </div>
          )}
        </div>
      )}

      {/* Placeholder for non-completed jobs */}
      {job.status !== 'completed' && (
        <div className="aspect-square flex items-center justify-center bg-[var(--bg-elevated)]">
          {job.status === 'processing' && (
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto text-[var(--accent)] animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="block mt-2 text-xs text-[var(--text-muted)]">Processing</span>
            </div>
          )}
          {job.status === 'pending' && (
            <div className="text-center">
              <span className="text-2xl font-light text-[var(--text-muted)]">#{job.position || '?'}</span>
              <span className="block text-xs text-[var(--text-muted)] mt-1">in queue</span>
            </div>
          )}
          {job.status === 'failed' && (
            <div className="text-center px-4">
              <svg className="w-8 h-8 mx-auto text-[var(--error)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <span className="block mt-2 text-xs text-[var(--error)] line-clamp-2">{job.error || 'Failed'}</span>
            </div>
          )}
          {job.status === 'cancelled' && (
            <span className="text-sm text-[var(--text-muted)]">Cancelled</span>
          )}
        </div>
      )}

      {/* Info */}
      <div className="p-3 space-y-2">
        <p className="text-sm text-[var(--text-secondary)] line-clamp-2 leading-snug">{job.prompt}</p>
        <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
          <span>{formatTime(job.created_at)}</span>
          <span>{job.width}x{job.height}</span>
        </div>

        {/* Actions */}
        {(job.status === 'pending' || job.status === 'processing' || job.status === 'failed') && (
          <div className="flex gap-2 pt-1">
            {job.status === 'pending' && onCancel && (
              <button
                onClick={(e) => { e.stopPropagation(); onCancel(job.job_id); }}
                className="btn btn-ghost flex-1 py-1.5 text-xs"
              >
                Cancel
              </button>
            )}
            {job.status === 'processing' && onCancel && (
              <button
                onClick={(e) => { e.stopPropagation(); onCancel(job.job_id, true); }}
                className="btn btn-ghost flex-1 py-1.5 text-xs text-[var(--error)]"
                title="Force cancel stuck job"
              >
                Force Cancel
              </button>
            )}
            {job.status === 'failed' && onRetry && (
              <button
                onClick={(e) => { e.stopPropagation(); onRetry(job.job_id); }}
                className="btn btn-ghost flex-1 py-1.5 text-xs text-[var(--accent)]"
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
